import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
}

/**
 * Error fallback component for react-error-boundary
 * Styled to match the app theme with dark mode support
 */
export default function ErrorFallback({
  error,
  resetErrorBoundary,
}: ErrorFallbackProps) {
  const isChunkError =
    error.name === "ChunkLoadError" ||
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.toLowerCase().includes("chunk");

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-surface border border-surface-border rounded-lg p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-8 h-8 text-error flex-shrink-0" />
          <div>
            <h2 className="text-xl font-bold text-white">
              {isChunkError ? "Update Required" : "Something Went Wrong"}
            </h2>
            <p className="text-sm text-muted mt-1">
              {isChunkError
                ? "The application has been updated. Please reload to continue."
                : "An error occurred while loading this component."}
            </p>
          </div>
        </div>

        {/* Error Details */}
        <div className="bg-surface-dark rounded-lg p-3 border border-surface-border">
          <p className="text-xs font-semibold text-white mb-1">Error:</p>
          <pre className="text-xs text-error font-mono whitespace-pre-wrap break-all">
            {error.message}
          </pre>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={handleReload} className="flex-1">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reload
          </Button>
          {!isChunkError && resetErrorBoundary && (
            <Button
              onClick={resetErrorBoundary}
              variant="outline"
              className="flex-1"
            >
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
